import { ReactNode, useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useMeetingTranscript } from 'modules/meetings/lib/hooks/useMeetingTranscript'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

interface TranscriptMessagesProps {
  children?: ReactNode
  autoScroll?: boolean
}

export const TranscriptMessages = ({ children, autoScroll = false }: TranscriptMessagesProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { meeting } = useMeeting()
  const { transcript } = useMeetingTranscript()
  const attendees = meeting?.attendees || []
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)

  const allAttendeesInPerson =
    attendees?.map((attendee) => ({
      id: String(attendee.id),
      firstName: attendee.employee?.firstName || i18n.t('meetings.unknown_participant'),
      lastName: attendee.employee?.lastName || '',
      src: buildUrl(attendee.employee?.avatar?.url),
    })) || []

  useEffect(() => {
    if (autoScroll && !isHovering && messagesContainerRef.current && transcript?.length) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [transcript, isHovering, autoScroll])

  return (
    <Box
      ref={messagesContainerRef}
      flexDirection="column"
      width="full"
      height="full"
      className={styles.hiddenScrollBar}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      padding="s24"
      gap="s24"
    >
      {children ?? children}

      {transcript?.map((utterance, index) => {
        const attendee = attendees?.find(({ id }) => Number(id) === Number(utterance.attendeeId))
        const fullName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
        const firstName = attendee?.employee?.firstName || i18n.t('meetings.unknown_participant')
        const lastName = attendee?.employee?.lastName || ''
        const avatar = attendee?.employee?.avatar?.url || ''
        const timestamp = moment(utterance.timestamp, 'X').locale(locale).format('LT')
        const inPerson = utterance.inPerson
        return (
          <Box key={index} flexDirection="column" gap="s8">
            {inPerson ? (
              <Box flexDirection="row" gap="s8" alignItems="center" width="full">
                <Box flexDirection="row">
                  {allAttendeesInPerson.map((att) => (
                    <F0AvatarPerson
                      key={att.id}
                      firstName={att.firstName}
                      lastName={att.lastName}
                      size="sm"
                      src={buildUrl(att.src)}
                    />
                  ))}
                </Box>
                <Text.Normal size="200" color="grey1000" weight="semibold">
                  {allAttendeesInPerson.map((att) => att.firstName).join(' & ')}
                </Text.Normal>
                <Text.Normal size="200" weight="normal">
                  •
                </Text.Normal>
                <Text.Normal size="100" color="grey600">
                  {timestamp}
                </Text.Normal>
              </Box>
            ) : (
              <Box flexDirection="row" gap="s8" alignItems="center">
                <F0AvatarPerson
                  firstName={firstName}
                  lastName={lastName}
                  size="sm"
                  src={buildUrl(avatar)}
                />
                <Text.Normal size="200" color="grey1000" weight="semibold">
                  {fullName}
                </Text.Normal>
                <Text.Normal size="200" weight="normal">
                  •
                </Text.Normal>
                <Text.Normal size="100" color="grey600">
                  {timestamp}
                </Text.Normal>
              </Box>
            )}
            <Text.Normal size="200" color="grey800">
              {utterance.utterance}
            </Text.Normal>
          </Box>
        )
      })}

      {(!transcript || transcript?.length === 0) && (
        <Text.Normal size="200" color="grey800">
          {i18n.t('meetings.room.transcript.no_transcript_available')}
        </Text.Normal>
      )}
    </Box>
  )
}
